// Phase 91 — Modal "Có phiên bản mới": hiện khi app native mở mà phiên bản
// cũ hơn latest backend trả về.
//
// v17.36 (Feb 2026) — CRITICAL FIX + FAIL-SAFE:
//   1. Gate PER PLATFORM: iOS và Android có version scheme độc lập, so sánh
//      với đúng field theo `window.__pickleagueNativePlatform`. Gọi
//      `/mobile/app-version?platform=ios|android` để BE trả `latest_version`
//      chính xác cho platform đó.
//   2. Modal CHỈ chặn (blocking) khi `force_update === true` trong cfg. Nếu
//      false → hiện banner nhẹ NON-BLOCKING (user vẫn dùng app được).
//   3. Fail-safe: thiếu bất kỳ dữ liệu nào (nativeVersion / latest_version /
//      không so sánh được) → KHÔNG hiện modal (thà bỏ sót còn hơn kẹt user).
//
// v17.37 (Feb 2026) — SAFETY VALVE (bug fix Android popup kẹt):
//   4. (ĐÃ GỠ ở v19.5 — xem dưới)
//   5. Nếu BE trả về latest_version <= nativeVersion (đã bằng hoặc mới hơn)
//      → KHÔNG hiện gate, kể cả force_update=true. (Đã có ở logic isVersionOlder,
//      chỉ document rõ intent.)
//
// v19.5 (24/08/2026) — BỎ NÚT HOÃN (quyết định user): modal blocking
// (force_update=true) KHÔNG còn nút "Đóng tạm 24 giờ" / "Đã cập nhật rồi".
// User BẮT BUỘC cập nhật app mới thì modal mới ẩn (khi nativeVersion >= latest).
// Snooze/dismiss localStorage chỉ còn hiệu lực cho banner NON-blocking.
import { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { api } from "../../lib/api";

// v17.38 — Dismiss BỀN VỮNG theo version: user bấm X ở banner non-blocking
// → ẩn cho đến khi BE bump version cao hơn cái đã dismiss.
const DISMISS_VERSION_KEY = "pl.appUpdateGate.dismissedVersion";

export function isVersionOlder(current, latest) {
  if (!current || !latest) return false;
  const a = String(current).split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(latest).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] || 0) < (b[i] || 0)) return true;
    if ((a[i] || 0) > (b[i] || 0)) return false;
  }
  return false;
}

export default function AppUpdateGate() {
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // v17.38 — Dismiss BỀN VỮNG theo version (chỉ áp dụng banner non-blocking).
  const [dismissedVersion, setDismissedVersion] = useState(() => {
    try { return window.localStorage.getItem(DISMISS_VERSION_KEY) || ""; } catch { return ""; }
  });

  const nativeVersion =
    typeof window !== "undefined" ? window.__pickleagueNativeAppVersion : null;
  const platform =
    typeof window !== "undefined" ? window.__pickleagueNativePlatform : null;

  useEffect(() => {
    if (!nativeVersion || !platform) return;
    // v17.36 — Truyền platform để BE trả latest_version đúng cho iOS/Android.
    api
      .get(`/mobile/app-version?platform=${encodeURIComponent(platform)}`)
      .then((res) => setCfg(res.data || null))
      .catch(() => {});
  }, [nativeVersion, platform]);

  // v17.36 — Fail-safe: thiếu bất kỳ dữ liệu nào → KHÔNG hiện gì cả.
  if (!nativeVersion || !platform || !cfg) return null;
  const latest = cfg.latest_version;
  if (!latest) return null;
  if (!isVersionOlder(nativeVersion, latest)) return null;
  // v19.5 — force_update=true → KHÔNG có bất kỳ cơ chế dismiss/snooze nào.
  // Modal chỉ ẩn khi user cập nhật app (nativeVersion >= latest).
  const isBlocking = !!cfg.force_update;
  if (!isBlocking) {
    // v17.38 — Dismiss theo version + dismiss session: CHỈ cho banner non-blocking.
    if (dismissedVersion && !isVersionOlder(dismissedVersion, latest)) return null;
    if (dismissed) return null;
    // v17.39 — Banner non-blocking CHỈ hiện khi admin bật `banner_enabled=true`.
    if (!cfg.banner_enabled) return null;
  }

  const storeUrl =
    cfg.store_url ||
    (platform === "ios" ? cfg.store_url_ios : cfg.store_url_android);

  const openStore = () => {
    if (!storeUrl) return;
    try {
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "OPEN_BROWSER", url: storeUrl })
        );
        return;
      }
    } catch {
      /* fallthrough */
    }
    window.location.href = storeUrl;
  };

  // v17.36 — Non-blocking banner: user có thể đóng, vẫn dùng app được.
  if (!isBlocking) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[600] w-[calc(100%-2rem)] max-w-sm rounded-xl bg-[#141D2E] border border-[#FF8A00]/40 shadow-2xl px-3.5 py-2.5 flex items-center gap-2.5"
        data-testid="app-update-gate-banner"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-black text-white">Có phiên bản mới ({latest})</div>
          <div className="text-[11px] text-white/60 truncate">Bấm để cập nhật nhanh.</div>
        </div>
        <button
          type="button"
          onClick={openStore}
          className="shrink-0 rounded-lg bg-[#FF8A00] hover:bg-[#FF9F1C] text-black font-black text-[11.5px] px-3 py-1.5 flex items-center gap-1"
          data-testid="app-update-gate-btn-soft"
        >
          <DownloadSimple size={13} weight="bold" /> Cập nhật
        </button>
        <button
          type="button"
          onClick={() => {
            // v17.38 — Ghi ĐÚNG version đang được advertise vào localStorage.
            // Chỉ hiện lại khi BE bump version cao hơn.
            try { window.localStorage.setItem(DISMISS_VERSION_KEY, latest); } catch { /* noop */ }
            setDismissedVersion(latest);
            setDismissed(true);
          }}
          className="shrink-0 p-1.5 text-white/50 hover:text-white/90"
          data-testid="app-update-gate-dismiss"
          aria-label="Ẩn thông báo"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    );
  }

  // Blocking modal (force_update = true) — v19.5: KHÔNG có nút hoãn/đóng.
  // Bắt buộc cập nhật app mới thì modal mới ẩn.
  return (
    <div
      className="fixed inset-0 z-[600] flex items-center justify-center bg-black/70 backdrop-blur-sm px-8"
      data-testid="app-update-gate"
    >
      <div className="relative w-full max-w-xs rounded-2xl bg-[#141D2E] border border-white/10 shadow-2xl p-5 text-center">
        <h2 className="text-[16px] font-black text-white mb-1.5">Có phiên bản mới ({latest})</h2>
        <p className="text-[13px] text-white/70 leading-relaxed mb-4">
          Bạn đang dùng v{nativeVersion}. Vui lòng cập nhật để tiếp tục sử dụng ứng dụng.
        </p>
        <button
          type="button"
          onClick={openStore}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#FF8A00] hover:bg-[#FF9F1C] text-black font-black text-[13.5px] py-2.5 transition-colors"
          data-testid="app-update-gate-btn"
        >
          <DownloadSimple size={16} weight="bold" />
          Cập nhật ngay
        </button>
      </div>
    </div>
  );
}
